import { useEffect, useState } from 'react';
import { Select, Loader } from '@mantine/core';
import {
  useAccountingAllocationTypeRepository,
  useAccountingDocumentRepository,
} from '@ayocore/erp/accounting/client/rpc';
import logger from '@ayocore/platform/common/helpers/logger';

interface AllocationTypeSelectProps {
  documentId: number;
  value: number | null;
  disabled?: boolean;
  onChanged?: () => void;
}

interface AllocationOption {
  id: number;
  name: string;
}

/**
 * Селектор типа разнесения документа.
 * Работает с нормализованной колонкой accounting_documents.allocation_type_id —
 * подходит для любого типа документа в модуле accounting.
 */
export function AllocationTypeSelect({
  documentId,
  value,
  disabled,
  onChanged,
}: AllocationTypeSelectProps) {
  const allocationRepo = useAccountingAllocationTypeRepository();
  const documentRepo = useAccountingDocumentRepository();

  const [options, setOptions] = useState<AllocationOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentValue, setCurrentValue] = useState<string | null>(
    value !== null ? String(value) : null
  );

  useEffect(() => {
    setCurrentValue(value !== null ? String(value) : null);
  }, [value]);

  useEffect(() => {
    const load = async () => {
      try {
        const rows = await allocationRepo.filterActive().fetch();
        setOptions((rows ?? []).map(r => ({ id: r.id as number, name: r.name as string })));
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const handleChange = async (val: string | null) => {
    setCurrentValue(val);
    try {
      setSaving(true);
      await documentRepo.update(documentId, {
        allocation_type_id: val !== null ? Number(val) : null,
      });
      onChanged?.();
    } catch (err) {
      logger.error('Ошибка сохранения типа разнесения:', err);
      setCurrentValue(value !== null ? String(value) : null);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader size='xs' />;

  return (
    <Select
      label='Тип разнесения'
      size='xs'
      data={options.map(o => ({ value: String(o.id), label: o.name }))}
      value={currentValue}
      onChange={handleChange}
      disabled={disabled || saving}
      clearable
      style={{ maxWidth: 280 }}
    />
  );
}
